"use client"

import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "cmdk"
import { Search } from "lucide-react"
import React, { ReactNode, useState } from "react"

import { CheckSolid } from "../../icons/check-solid"
import * as autoCompleteStyle from "../Input/auto-complete.styled"
import { Popover, PopoverContent, PopoverTrigger } from "../Popover/popover"
import * as styles from "./combobox.styled"

export type Option = Record<"value" | "label", string> & Record<string, string>

type ComboboxProps<T extends Option> = {
  value: T
  onValueChange: (value: T) => void
  options: T[]
  children: ({ selectedOption, isOpen }: { selectedOption?: T; isOpen: boolean }) => ReactNode
  renderOption: ({ option, isSelected }: { option: T; isSelected: boolean }) => ReactNode
  renderValue: (option: T) => string
  emptyMessage: string
  placeholder?: string
  className?: string
}

/**
 * Combobox is a component that allows the user to select an option from a list of options with a search input.
 * @param props The children is the trigger of the popover. The children must be a function that returns a ReactNode.
 * @returns A Combobox component.
 *
 * @example
 * ```tsx
 * <Combobox
 *  value={value}
 *  onValueChange={setValue}
 *  options={options}
 *  renderOption={({ option }) => option.label}
 *  renderValue={({ option }) => option.value}
 *  emptyMessage="No results"
 *  >
 *  {({ selectedOption, isOpen }) => (
 *     <Button>
 *      <span>{selectedOption?.label ?? "Select an option"}</span>
 *      <ChevronDown
 *        className={cn("h-4 w-4 shrink-0 opacity-50", isOpen ? "rotate-180" : null)}
 *      />
 *    </Button>
 *  )}
 * </Combobox>
 * ```
 */
export function Combobox<T extends Option>({
  value,
  onValueChange,
  options,
  children,
  renderOption,
  renderValue,
  placeholder,
  emptyMessage,
  className,
}: ComboboxProps<T>) {
  const [isOpen, setOpen] = useState(false)
  const selectedOption = options.find((option) => option.value === value.value)

  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children({ selectedOption, isOpen })}</PopoverTrigger>
      <PopoverContent align="start" className={styles.popoverContent(className)}>
        <Command>
          <div className={styles.inputWrapper}>
            <Search className={styles.searchIcon} />
            <CommandInput placeholder={placeholder} className={styles.input} />
          </div>
          <CommandGroup className={autoCompleteStyle.commandGroup}>
            {options.map((option) => {
              const isSelected = value.value === option.value
              return (
                <CommandItem
                  key={option.value}
                  value={renderValue(option)}
                  onSelect={() => {
                    onValueChange(option)
                    setOpen(false)
                  }}
                  className={autoCompleteStyle.commandItem(isSelected)}
                >
                  {renderOption({ option, isSelected: isSelected })}
                  {isSelected ? <CheckSolid className={autoCompleteStyle.selectedIcon} /> : null}
                </CommandItem>
              )
            })}
          </CommandGroup>
          <CommandEmpty className={autoCompleteStyle.commandEmpty()}>{emptyMessage}</CommandEmpty>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
